import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Moment from 'moment';
import flatten from 'lodash/flatten';
import reverse from 'lodash/reverse';
import uniq from 'lodash/uniq';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import keys from 'lodash/keys';
import DocItem from './DocItem';
import AccountDocs from './AccountDocs';

import {
  fetchDownloads,
  createAuthToken,
  createAccountDownloadLink,
  createUserDownloadLink,
  FETCH_CACHE_DURATION,
} from '../../actions';


import './styles.css';
import { folioDocTypes, folioLookup } from '../Folio/lookups';
import { formatQuantity } from '../SecurityDetail/SecurityDetail';

const ALL_DOCS = 'All Documents';
const defTypeOpts = [ALL_DOCS];
let shownCount = 0; // Count the number of doc items displayed after filters


const getAccountDocYearList = (
  documents,
  year,
  filterFrom,
  filterTo,
  filterItemType,
) => {
  const filteredDocs = documents.filter((doc) => {
    const mmt = Moment(doc.docDate);
    return doc.type !== 'Bills' && (filterItemType === ALL_DOCS ||
      filterItemType === folioLookup(folioDocTypes, doc.type).name)
      && mmt.year() === year
      && mmt.isBetween(filterFrom, filterTo, 'day', '[]');
  });

  shownCount += filteredDocs.length;

  return (
    filteredDocs.length > 0 ?
      <div key={`grp${year}`} styleName="yearGroup">{year}
        {filteredDocs.map((doc) => {
        const mmt = Moment(doc.docDate);
        const showViewIcon = doc.type && doc.type.toUpperCase() !== 'TXF';
        return (
          <DocItem
            key={doc.reportId}
            type={doc.type}
            display={folioLookup(folioDocTypes, doc.type).display}
            date={mmt.format('MMM D')}
            viewLink={doc.viewLink}
            downloadLink={doc.downloadLink}
            showViewIcon={showViewIcon}
          />
        );
        })}
      </div> : null
  );
};


const AccountDocList = ({
  documents,
  yearOpts,
  filterFrom,
  filterTo,
  filterItemType,
}) => {
  const filteredYears = yearOpts.filter(year => year >= (
    String(Moment(filterFrom).year()) && year <= String(Moment(filterTo).year())
  ));

  if (filteredYears.length < 1) {
    return (
      <div styleName="message">
        No statements or documents
      </div>
    );
  }

  shownCount = 0;

  const docList = filteredYears.sort((a, b) => {
    if (Moment(a).isBefore(Moment(b))) return 1;
    if (Moment(b).isBefore(Moment(a))) return -1;
    return 0;
  }).map(year => (
    getAccountDocYearList(documents, year, filterFrom, filterTo, filterItemType)
  ));

  return (
    <div>
      {documents && documents.length > 0
        && shownCount < documents.length ?
          <div styleName="countMessage">
            Showing {formatQuantity(shownCount)} of {formatQuantity(documents.length)} documents
          </div>
          : null}
      {docList}
    </div>
  );
};

export class UnconnectedAccountDocsContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filterItemType: ALL_DOCS,
      filterFrom: new Moment().format('YYYY-MM-DD'),
      filterTo: new Moment().format('YYYY-MM-DD'),
      itemTypeOpts: defTypeOpts,
      documents: [],
      yearOpts: [],
      minDate: new Moment().format('YYYY-MM-DD'),
      maxDate: new Moment().format('YYYY-MM-DD'),
      loading: true,
    };
  }

  componentDidMount() {
    const {
      userDownloads,
      userDownloadsFetchedAt,
      accountDownloads,
      accountDownloadsFetchedAt,
    } = this.props;
    if ((new Date() - userDownloadsFetchedAt) < FETCH_CACHE_DURATION ||
      (new Date() - accountDownloadsFetchedAt) < FETCH_CACHE_DURATION) {
      this.onDocs(userDownloads, accountDownloads);
    } else {
      this.props.fetchDownloads();
    }
  }

  componentWillReceiveProps({
    userDownloads,
    userDownloadsFetchedAt,
    accountDownloads,
    accountDownloadsFetchedAt,
  }) {
    if ((userDownloadsFetchedAt !== this.props.userDownloadsFetchedAt
        && keys(userDownloads).length > 0) ||
      (accountDownloadsFetchedAt !== this.props.accountDownloadsFetchedAt
        && keys(accountDownloads).length > 0)) {
      this.onDocs(userDownloads, accountDownloads);
    }
  }

  async onDocs(userDownloads, accountDownloads) {
    const newItemTypes = uniq([
      ...defTypeOpts,
      ...this.getFilterTypesFromDownloads(userDownloads),
      ...this.getFilterTypesFromDownloads(accountDownloads),
    ]);

    // Convert accountDownloads (which only has type and year)
    // {
    //   8948: [
    //     2017,
    //     2016,
    //   ],
    //   TXF: [
    //     2017,
    //     2016,
    //   ],
    // }
    // into a list of objects that look like:
    // [
    //  { supertype: 'account', type: '8948', docDate: '2017-12-31', reportId: '8948-2017' },
    //  { supertype: 'account', type: '8948', docDate: '2016-12-31', reportId: '8948-2016' },
    //  { supertype: 'account', type: 'TXF', docDate: '2017-12-31', reportId: 'TXF-2016' },
    //  { supertype: 'account', type: 'TXF', docDate: '2016-12-31', reportId: 'TXF-2016' },
    // ]
    const accountDocs = flatten(Object.keys(accountDownloads).map(type => (
      flatten(accountDownloads[type].map(year => ({
        year,
        supertype: 'account',
        type,
        docDate: `${year}-12-31`,
        reportId: `${type}-${year}`,
      })))
    )));

    // Get all documents as a single array of objects with a type and docDate field
    const userDocs = flatten((this.getTypesFromDownloads(userDownloads)).map(type => (
      this.getDocsFromUserDownloadsByType(userDownloads[type], type)
    )));

    const docs = userDocs.concat(accountDocs);
    const authToken = await this.props.createAuthToken({
      expiresIn: 30 * 60, // 30 minutes
      scope: ['downloads:read'],
    });
    const allDocs = await Promise.all(docs.map(async (doc) => {
      let viewLink;
      let downloadLink;
      if (doc.supertype === 'account') {
        viewLink = await this.props.createAccountDownloadLink(authToken, doc.type, doc.year);
        downloadLink = await this.props.createAccountDownloadLink(
          authToken,
          doc.type,
          doc.year,
          { download: true },
        );
      } else if (doc.supertype === 'user') {
        viewLink = await this.props.createUserDownloadLink(authToken, doc.type, doc.reportId);
        downloadLink = await this.props.createUserDownloadLink(
          authToken,
          doc.type,
          doc.reportId,
          { download: true },
        );
      } else {
        throw new Error(`Unknown doc supertype: ${doc.supertype}`);
      }
      return {
        ...doc,
        viewLink,
        downloadLink,
      };
    }));

    // Get all dates from all document types as a single array of strings
    const newDates = this.getDatesFromDocs(allDocs);
    const newYears = reverse(uniq(this.getYearsFromDocs(allDocs)));
    const minDate = Moment.min(newDates.map(date => (Moment(date)))).format('YYYY-MM-DD');
    const maxDate = Moment.max(newDates.map(date => (Moment(date)))).format('YYYY-MM-DD');

    this.setState({
      ...this.state,
      filterFrom: minDate,
      filterTo: maxDate,
      itemTypeOpts: newItemTypes,
      yearOpts: newYears,
      minDate,
      maxDate,
      documents: allDocs.sort((a, b) => {
        if (Moment(a.docDate).isBefore(Moment(b.docDate))) return 1;
        if (Moment(b.docDate).isBefore(Moment(a.docDate))) return -1;
        return 0;
      }),
      loading: false,
    });
  }

  getDocsFromUserDownloadsByType = (documents, type) => (
    documents.map(doc => ({
      ...doc,
      supertype: 'user',
      type,
      docDate: doc.reportDate,
    }))
  )
  getTypesFromDownloads = downloads => (Object.keys(downloads).filter(key =>
    (isArray(downloads[key]) && downloads[key].length > 0)))
  getFilterTypesFromDownloads = downloads => (Object.keys(downloads).filter(key =>
    (isArray(downloads[key]) && downloads[key].length > 0)).map(key =>
    (folioLookup(folioDocTypes, key).name)))
  getDatesFromDocs = (documents) => {
    const newItems = [];
    documents.forEach((doc) => {
      const mmt = new Moment(doc.docDate);
      if (mmt.isValid()) newItems.push(doc.docDate);
    });
    return (newItems);
  }
  getYearsFromDocs = (documents) => {
    const newItems = [];
    documents.forEach((doc) => {
      const mmt = new Moment(doc.docDate);
      if (mmt.isValid()) newItems.push(mmt.year());
    });
    return (newItems);
  }
  filterItemType = (event) => {
    this.setState({
      ...this.state,
      filterItemType: event.target.value,
    });
  }
  filterFrom = (value) => {
    const momentValue = Moment(value);
    if (momentValue.isAfter(this.state.filterTo)) {
      this.setState({
        ...this.state,
        filterTo: Moment(value).format('YYYY-MM-DD'),
        filterFrom: Moment(value).format('YYYY-MM-DD'),
      });
    } else {
      this.setState({
        ...this.state,
        filterFrom: Moment(value).format('YYYY-MM-DD'),
      });
    }
  }
  filterTo = (value) => {
    const momentValue = Moment(value);
    if (momentValue.isBefore(this.state.filterFrom)) {
      this.setState({
        ...this.state,
        filterTo: Moment(value).format('YYYY-MM-DD'),
        filterFrom: Moment(value).format('YYYY-MM-DD'),
      });
    } else {
      this.setState({
        ...this.state,
        filterTo: Moment(value).format('YYYY-MM-DD'),
      });
    }
  }
  render() {
    const {
      documents,
      yearOpts,
      filterFrom,
      filterTo,
      filterItemType,
    } = this.state;
    const {
      mediaQueryValues,
    } = this.props;
    const propsToPass = {
      itemTypeOpts: this.state.itemTypeOpts,
      itemTypeSelected: this.state.filterItemType,
      onItemTypeChange: this.filterItemType,
      dateMin: this.state.minDate,
      fromSelected: this.state.filterFrom,
      onFromChange: this.filterFrom,
      dateMax: this.state.maxDate,
      toSelected: this.state.filterTo,
      onToChange: this.filterTo,
      mediaQueryValues,
    };
    return (
      <div>
        <AccountDocs {...propsToPass}>
          {this.state.loading ? (
            <div styleName="message">Loading...</div>
          ) : (
            <div>
              <AccountDocList
                documents={documents}
                yearOpts={yearOpts}
                filterFrom={filterFrom}
                filterTo={filterTo}
                filterItemType={filterItemType}
              />
            </div>
          )}
        </AccountDocs>
      </div>
    );
  }
}

UnconnectedAccountDocsContainer.propTypes = {
  mediaQueryValues: PropTypes.shape({}),
};

UnconnectedAccountDocsContainer.defaultProps = {
  mediaQueryValues: {},
};

const mapStateToProps = ({
  currentAccount,
  userDownloads,
  accountDownloadsByAccount,
}) => ({
  userDownloadsFetchedAt: get(userDownloads, 'fetchedAt', new Date() - (FETCH_CACHE_DURATION + 1)),
  userDownloads: get(userDownloads, 'data', {}),
  accountDownloadsFetchedAt: get(accountDownloadsByAccount, `${currentAccount}.fetchedAt`, new Date() - (FETCH_CACHE_DURATION + 1)),
  accountDownloads: get(accountDownloadsByAccount, `${currentAccount}.data`, {}),
});


const mapDispatchToProps = ({
  fetchDownloads,
  createAuthToken,
  createAccountDownloadLink,
  createUserDownloadLink,
});


export default connect(mapStateToProps, mapDispatchToProps)(UnconnectedAccountDocsContainer);



// WEBPACK FOOTER //
// ./src/components/AccountDocs/index.js